import React, { useEffect, useRef, useState, useCallback } from "react";
import { setAppBit } from "../api/sacada";
import useWebSocket from "../hooks/useWebSockets";
import { useAuth } from "../context/AuthContext";
const TEAL      = "#057c82";
const TEAL_BG   = "#f0f8f8";
const BORDER2   = "#d0e8ea";
const GREEN     = "#27ae60";
const ORANGE    = "#e67e22";
const RED       = "#e74c3c";
const GRAY_L    = "#888";

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&display=swap');
.br-root *, .br-root *::before, .br-root *::after {
  box-sizing: border-box;
  font-family: 'Rajdhani','Segoe UI',sans-serif;
}
.glass-card {
  background: rgba(255,255,255,0.85);
  border: 1px solid rgba(184,216,218,0.75);
  border-radius: 12px;
  backdrop-filter: blur(6px);
  box-shadow: 0 4px 16px rgba(5,124,130,0.07);
  padding: 12px;
}
.s-title {
  font-size: 9px; font-weight: 800; color: #057c82;
  letter-spacing: 2px; text-transform: uppercase;
  border-bottom: 1px solid #d0e8ea;
  padding-bottom: 5px; margin-bottom: 9px;
}
@keyframes glow-red    { 0%,100%{box-shadow:0 0 7px 3px #e74c3c55} 50%{box-shadow:0 0 16px 6px #e74c3c44} }
@keyframes glow-green  { 0%,100%{box-shadow:0 0 7px 3px #27ae6055} 50%{box-shadow:0 0 16px 6px #27ae6044} }
@keyframes glow-orange { 0%,100%{box-shadow:0 0 7px 3px #e67e2255} 50%{box-shadow:0 0 16px 6px #e67e2244} }
@keyframes glow-teal   { 0%,100%{box-shadow:0 0 7px 3px #057c8255} 50%{box-shadow:0 0 16px 6px #057c8244} }
@keyframes pulse-led   { 0%{transform:scale(1);opacity:.7} 100%{transform:scale(1.9);opacity:0} }
@keyframes notif-in    { from{opacity:0;transform:translateY(-14px)} to{opacity:1;transform:none} }
`;

function pad(n) { return String(n).padStart(2,"0"); }
function nowTime() {
  const d = new Date();
  return pad(d.getHours())+":"+pad(d.getMinutes())+":"+pad(d.getSeconds());
}
function formatDuration(totalSeconds) {
  const h = Math.floor(totalSeconds/3600);
  const m = Math.floor((totalSeconds%3600)/60);
  const s = totalSeconds%60;
  return pad(h)+":"+pad(m)+":"+pad(s);
}

function Indicator({ on, color, title, sub }) {
  const cv   = color==="red" ? RED : color==="orange" ? ORANGE : GREEN;
  const anim = "glow-"+(color==="red"?"red":color==="orange"?"orange":"green");
  return (
    <div style={{
      display:"flex", alignItems:"center", gap:9,
      padding:"8px 10px", background:TEAL_BG,
      border:"1px solid "+BORDER2, borderRadius:8, marginBottom:6,
    }}>
      <div style={{ position:"relative", flexShrink:0 }}>
        <div style={{
          width:14, height:14, borderRadius:"50%",
          background: on ? cv : "#ddeaec",
          border:"2px solid "+(on ? cv : "#b8d8da"),
          animation: on ? anim+" 1.4s infinite" : "none",
          transition:"background .3s",
        }}/>
        {on && <div style={{
          position:"absolute", top:-3, left:-3, width:20, height:20, borderRadius:"50%",
          border:"2px solid "+cv+"44", animation:"pulse-led 1.4s infinite",
        }}/>}
      </div>
      <div>
        <div style={{ fontSize:11, fontWeight:700, color:"#333" }}>{title}</div>
        {sub && <div style={{ fontSize:9, color:GRAY_L, marginTop:1 }}>{sub}</div>}
      </div>
    </div>
  );
}

/* ── Tuile pleine ON/OFF (fin de course, sorties moteur) ── */
function StateTile({ on, color, title, sub }) {
  const cv = color==="red" ? RED : color==="orange" ? ORANGE : color==="teal" ? TEAL : GREEN;
  const anim = "glow-"+(color==="red"?"red":color==="orange"?"orange":color==="teal"?"teal":"green");
  return (
    <div style={{
      display:"flex", alignItems:"center", justifyContent:"space-between", gap:10,
      padding:"9px 11px", marginBottom:6, borderRadius:9,
      background: on ? cv+"17" : TEAL_BG,
      border:"1.5px solid "+(on ? cv : BORDER2),
      animation: on ? anim+" 1.8s infinite" : "none",
      transition:"background .25s, border-color .25s",
    }}>
      <div>
        <div style={{ fontSize:11, fontWeight:800, color: on ? cv : "#444" }}>{title}</div>
        {sub && <div style={{ fontSize:9, color:GRAY_L, marginTop:1 }}>{sub}</div>}
      </div>
      <div style={{
        fontSize:9, fontWeight:800, letterSpacing:1,
        padding:"3px 9px", borderRadius:20, flexShrink:0,
        background: on ? cv : "#ddeaec",
        color: on ? "#fff" : "#9bb0b2",
        transition:"background .25s, color .25s",
      }}>{on ? "ON" : "OFF"}</div>
    </div>
  );
}

function StatusRow({ label, value, valColor }) {
  return (
    <div style={{
      display:"flex", justifyContent:"space-between", alignItems:"center",
      background:TEAL_BG, border:"1px solid "+BORDER2,
      borderRadius:7, padding:"6px 9px", marginBottom:5,
    }}>
      <span style={{ fontSize:10, fontWeight:700, color:"#444" }}>{label}</span>
      <span style={{ fontSize:10, fontWeight:800, color:valColor||TEAL }}>{value}</span>
    </div>
  );
}

/* ── SVG Barrier Scene ── */
function BarrierScene({ angle, carX, carVisible, opening, closing }) {
  const pvX = 300, pvY = 148, armLen = 260;

  const stoppedBetweenLimits = !opening && !closing && angle>1 && angle<89;
  const stateColor = angle>=89 ? GREEN : (opening||closing||stoppedBetweenLimits) ? ORANGE : RED;
  const stateLabel =
    opening ? "OUVERTURE EN COURS" :
    closing ? "FERMETURE EN COURS" :
    stoppedBetweenLimits ? "ARRÊT INTERMÉDIAIRE" :
    angle>=89 ? "BARRIÈRE OUVERTE" : "BARRIÈRE FERMÉE";

  const stripes = [];
  for (let i=0;i<8;i++) {
    if (i%2===0) continue;
    const t0=i/8, t1=(i+0.46)/8;
    stripes.push(
      <line key={i}
        x1={pvX+armLen*t0} y1={pvY}
        x2={pvX+armLen*t1} y2={pvY}
        stroke={RED} strokeWidth={11} strokeLinecap="butt"/>
    );
  }

  return (
    <svg width="100%" height="100%" viewBox="0 0 640 280"
      preserveAspectRatio="xMidYMid meet" style={{ display:"block" }}>
      <defs>
        <filter id="ds"><feDropShadow dx="0" dy="3" stdDeviation="5" floodColor="#000" floodOpacity=".2"/></filter>
        <clipPath id="sc"><rect width="640" height="280"/></clipPath>
        <linearGradient id="skyg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#c8e8f4"/>
          <stop offset="100%" stopColor="#dff5f8"/>
        </linearGradient>
      </defs>
      <g clipPath="url(#sc)">
        {/* SKY */}
        <rect x="0" y="0" width="640" height="280" fill="url(#skyg)"/>

        {/* HEDGE LEFT */}
        <rect x="0" y="84" width="268" height="70" fill="#3d7a3d"/>
        <rect x="0" y="76" width="268" height="18" fill="#4d9a4d"/>
        {Array.from({length:14}).map((_,i)=>(
          <ellipse key={i} cx={i*20} cy={78+(i%4)*5} rx={20} ry={16} fill="#2d6a2d" opacity=".5"/>
        ))}

        {/* HEDGE RIGHT */}
        <rect x="348" y="84" width="292" height="70" fill="#3d7a3d"/>
        <rect x="348" y="76" width="292" height="18" fill="#4d9a4d"/>
        {Array.from({length:16}).map((_,i)=>(
          <ellipse key={i} cx={350+i*20} cy={78+(i%4)*5} rx={20} ry={16} fill="#2d6a2d" opacity=".5"/>
        ))}

        {/* ROAD */}
        <rect x="0" y="152" width="640" height="128" fill="#5c6064"/>
        <rect x="0" y="150" width="640" height="5" fill="#9aa0a4"/>
        <rect x="0" y="272" width="640" height="8" fill="#383c3f"/>
        {Array.from({length:12}).map((_,i)=>(
          <rect key={i} x={i*58} y={198} width={36} height={6} rx="3" fill="#fff" opacity=".5"/>
        ))}

        {/* CROUZET CABINET */}
        <g filter="url(#ds)">
          <rect x="242" y="108" width="72" height="164" rx="10" fill="#1a2a3e"/>
          <rect x="251" y="119" width="54" height="144" rx="7" fill="#1e7fc8"/>
          <rect x="251" y="119" width="54" height="34" rx="7" fill="rgba(255,255,255,0.13)"/>
          <rect x="259" y="136" width="38" height="4" rx="2" fill="rgba(255,255,255,0.2)"/>
          <rect x="259" y="146" width="38" height="4" rx="2" fill="rgba(255,255,255,0.13)"/>
          <text x="278" y="176" textAnchor="middle" fill="#ffffff"
            fontSize="11" fontWeight="800" letterSpacing="1.2" fontFamily="Rajdhani,sans-serif">CROUZET</text>
          <text x="278" y="190" textAnchor="middle" fill="#cce8ff"
            fontSize="7.5" fontWeight="700" letterSpacing="1" fontFamily="Rajdhani,sans-serif">AUTOMATION</text>
          <circle cx="264" cy="204" r="4.5" fill="#00ff88" opacity=".9"/>
          <circle cx="278" cy="204" r="4.5" fill="#ffcc00" opacity=".85"/>
          <circle cx="292" cy="204" r="4.5" fill="#ff4444" opacity=".85"/>
          <rect x="236" y="270" width="84" height="8" rx="4" fill="#0a1a2a"/>
        </g>

        {/* MOTOR DISC */}
        <circle cx={pvX} cy={pvY} r={22} fill="#111" filter="url(#ds)"/>
        <circle cx={pvX} cy={pvY} r={12} fill="#666"/>
        <circle cx={pvX} cy={pvY} r={5}  fill="#333"/>

        {/* RIGHT SENSOR POST */}
        <g filter="url(#ds)">
          <rect x="337" y="138" width="18" height="136" rx="4" fill="#c8d4d6" stroke="#8aaaae" strokeWidth="1.5"/>
          <rect x="330" y="123" width="32" height="22" rx="5" fill="#1e2a31"/>
          <circle cx="346" cy="134" r="6"
            fill={angle>=89 ? GREEN : "#3a3a3a"}
            style={{ filter: angle>=89 ? "drop-shadow(0 0 5px #27ae60)" : "none" }}/>
          <rect x="330" y="270" width="32" height="7" rx="3" fill="#1e2a31"/>
        </g>

        {/* BARRIER ARM — interpolation visuelle entre deux états backend */}
        <g style={{
          transform:`rotate(${-angle}deg)`,
          transformOrigin:`${pvX}px ${pvY}px`,
          transition:"transform 360ms linear",
        }}>
          <line x1={pvX} y1={pvY+5} x2={pvX+armLen+4} y2={pvY+5}
            stroke="rgba(0,0,0,0.16)" strokeWidth={14} strokeLinecap="round"/>
          <line x1={pvX} y1={pvY} x2={pvX+armLen} y2={pvY}
            stroke="#eeeeee" strokeWidth={11} strokeLinecap="round"/>
          {stripes}
          <circle cx={pvX} cy={pvY} r={8} fill="#1a1a1a"/>
          <circle cx={pvX+armLen} cy={pvY} r={7} fill={RED}/>
        </g>

        {/* CAR */}
        {carVisible && (
          <g style={{
            transform:`translate(${carX}px, 162px)`,
            transition:"transform 360ms linear",
          }}>
            <ellipse cx="40" cy="64" rx="48" ry="6" fill="#000" opacity=".17"/>
            <rect x="2" y="22" width="78" height="38" rx="10" fill="#1a7a96" stroke="#0a5a72" strokeWidth="2"/>
            <path d="M18 22 C24 5, 60 5, 66 22 Z" fill="#a0cce0" stroke="#0a5a72" strokeWidth="2"/>
            <path d="M22 22 C26 10, 58 10, 62 22 Z" fill="#d0ecf8" opacity=".9"/>
            <rect x="24" y="25" width="14" height="10" rx="2" fill="#e0f4fc" opacity=".85"/>
            <rect x="46" y="25" width="14" height="10" rx="2" fill="#e0f4fc" opacity=".85"/>
            <circle cx="20" cy="60" r="10" fill="#222"/>
            <circle cx="20" cy="60" r="4.5" fill="#999"/>
            <circle cx="62" cy="60" r="10" fill="#222"/>
            <circle cx="62" cy="60" r="4.5" fill="#999"/>
            <rect x="78" y="30" width="6" height="8" rx="2" fill="#ffe870"/>
            <rect x="-1" y="30" width="5" height="8" rx="2" fill="#ff3333" opacity=".7"/>
          </g>
        )}

        {/* STATE BADGE */}
        <rect x="220" y="8" width="200" height="26" rx="13"
          fill="rgba(255,255,255,.92)" stroke="#b8d8da" strokeWidth="1.5"/>
        <text x="320" y="25" textAnchor="middle" fill={stateColor}
          fontSize="12" fontWeight="800" fontFamily="Rajdhani,sans-serif">{stateLabel}</text>

        {/* ANGLE BADGE */}
        <rect x="6" y="8" width="62" height="22" rx="11" fill="rgba(5,124,130,.9)"/>
        <text x="37" y="23" textAnchor="middle" fill="#fff"
          fontSize="10" fontWeight="700" fontFamily="Rajdhani,monospace">{Math.round(angle)}°</text>
      </g>
    </svg>
  );
}

export default function Barriere({ inactivePreview = false }) {
  const { token } = useAuth();
  const { data, lastMessage, connected, stale, error } = useWebSocket("barriere", !inactivePreview);
  const [notifs,     setNotifs]     = useState([]);
  const [clock,      setClock]      = useState(nowTime());
  const [isSending, setIsSending] = useState(false);
  const [, setLastCommandLatencyMs] = useState(null);

  const notifId  = useRef(0);
  const pendingCommandTraces = useRef(new Map());
  const prevCapteurE = useRef(false);
  const prevFcOpen   = useRef(false);
  const prevFcClose  = useRef(false);

  useEffect(()=>{
    const id=setInterval(()=>setClock(nowTime()),1000);
    return ()=>clearInterval(id);
  },[]);

  const addNotif = useCallback((text, type)=>{
    const id = ++notifId.current;
    setNotifs(n=>[...n,{ id, text, type:type||"green", time:nowTime() }]);
    setTimeout(()=>setNotifs(n=>n.filter(x=>x.id!==id)), 4000);
  },[]);

  const dataSource = lastMessage?.data_source;
  const isSimulation = dataSource === "simulation";
  const dataSourceLabel = isSimulation ? "SIMULATION" : dataSource === "plc" ? "PLC" : "INCONNUE";
  const commandsEnabled = lastMessage?.type === "live_data"
    && lastMessage?.commands_enabled === true;
  const controlsDisabled = !connected || stale || data === null || !commandsEnabled || isSending;

  // Les animations utilisent exclusivement l'état partagé du backend.
  const capteurE = data?.capteur_entree ?? false;
  const capteurS = data?.capteur_sortie ?? false;
  const fcOpen   = data?.fin_course_ouverture ?? false;
  const fcClose  = data?.fin_course_fermeture ?? false;
  const ledRouge = data?.led_rouge ?? false;
  const ledVerte = data?.led_vert ?? false;
  const motorS1  = data?.motor_sens1 ?? false;
  const motorS2  = data?.motor_sens2 ?? false;
  const opening  = motorS1;
  const closing  = motorS2;
  const motorsIdle = !motorS1 && !motorS2;
  const positionPercent = data?.position_percent;
  const positionSource = data?.position_source;
  const angle = positionPercent == null
    ? (fcOpen ? 90 : 0)
    : Math.max(0, Math.min(90, positionPercent * 0.9));
  const vehicleProgress = data?.vehicle_progress_percent;
  const carVisible = data?.vehicle_visible === true && vehicleProgress != null;
  const carX = carVisible ? -110 + (870 * vehicleProgress / 100) : -110;
  const entryCount = data?.vehicle_count;
  const elapsed = data?.operating_time_seconds;
  const positionQualifier = positionSource === "estimated"
    ? " — ESTIMÉE"
    : positionSource === "estimated_stop"
    ? " — ARRÊT INTERMÉDIAIRE ESTIMÉ"
    : positionSource === "limit_switch"
    ? " — FIN DE COURSE"
    : positionSource === "simulation"
    ? " — SIMULÉE"
    : "";
  const positionLabel = positionPercent == null
    ? (fcOpen ? "100% (90°) — FIN DE COURSE" : fcClose ? "0% (0°) — FIN DE COURSE" : "NON MESURÉE")
    : `${Math.round(positionPercent)}% (${Math.round(angle)}°)${positionQualifier}`;
  const stoppedBetweenLimits = motorsIdle
    && positionPercent != null
    && positionPercent > 0
    && positionPercent < 100;

  const sendTracedCommand = useCallback(async (bit, state) => {
    const correlationId = window.crypto?.randomUUID?.()
      || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    pendingCommandTraces.current.set(correlationId, performance.now());
    try {
      return await setAppBit("barriere", bit, state, token, correlationId);
    } catch (commandError) {
      pendingCommandTraces.current.delete(correlationId);
      throw commandError;
    }
  }, [token]);

  useEffect(() => {
    for (const trace of lastMessage?.command_traces || []) {
      const startedAt = pendingCommandTraces.current.get(trace.correlation_id);
      if (startedAt === undefined) continue;
      pendingCommandTraces.current.delete(trace.correlation_id);
      setLastCommandLatencyMs(Math.round(performance.now() - startedAt));
    }
  }, [lastMessage]);

  const detectEntry = useCallback(async () => {
    if (controlsDisabled) return;
    setIsSending(true);
    try {
      await sendTracedCommand(0, true);
      addNotif("Véhicule détecté à l'entrée", "green");
      await new Promise(resolve => setTimeout(resolve, 500));
      await sendTracedCommand(0, false);
    } catch (err) {
      addNotif("Commande d'entrée refusée ou indisponible", "red");
    } finally {
      setIsSending(false);
    }
  }, [addNotif, controlsDisabled, sendTracedCommand]);

  const detectExit = useCallback(async () => {
    if (controlsDisabled) return;
    setIsSending(true);
    try {
      await sendTracedCommand(1, true);
      addNotif("Véhicule détecté à la sortie", "orange");
      await new Promise(resolve => setTimeout(resolve, 500));
      await sendTracedCommand(1, false);
    } catch (err) {
      addNotif("Commande de sortie refusée ou indisponible", "red");
    } finally {
      setIsSending(false);
    }
  }, [addNotif, controlsDisabled, sendTracedCommand]);

  // Suivi du front montant réel du capteur (informatif uniquement — l'affichage
  // et le compteur sont désormais gérés directement au clic, voir detectEntry).
  useEffect(() => {
    prevCapteurE.current = capteurE;
  }, [capteurE]);

  useEffect(() => {
    if (fcOpen && !prevFcOpen.current) {
      addNotif("Fin de course ouverture détectée", "green");
    }
    prevFcOpen.current = fcOpen;
  }, [fcOpen, addNotif]);

  useEffect(() => {
    if (fcClose && !prevFcClose.current) {
      addNotif("Fin de course fermeture détectée", "red");
    }
    prevFcClose.current = fcClose;
  }, [fcClose, addNotif]);

  const sysLabel =
    opening ? "OUVERTURE EN COURS" :
    closing ? "FERMETURE EN COURS" :
    stoppedBetweenLimits ? "ARRÊT INTERMÉDIAIRE" :
    fcOpen  ? "BARRIÈRE OUVERTE"   :
    fcClose ? "ÉTAT INITIAL"       : "—";

  const vehicleLabel =
    capteurE   ? "À L'ENTRÉE"       :
    opening    ? "EN PASSAGE"       :
    capteurS   ? "SORTIE DÉTECTÉE"  :
    carVisible ? "EN ATTENTE"       : "AUCUN";

  const sysColor = stoppedBetweenLimits ? ORANGE : fcClose ? RED : fcOpen ? GREEN : ORANGE;
  const ntC = {green:GREEN, blue:"#2980b9", orange:ORANGE, red:RED};

  return (
    <div className="br-root" style={{background:TEAL_BG, minHeight:"100vh", padding:0}}>
      <style>{STYLES}</style>

      {/* NOTIFICATIONS flottantes */}
      <div style={{
        position:"fixed", top:110, left:"50%", transform:"translateX(-50%)",
        zIndex:999, display:"flex", flexDirection:"column", gap:5,
        alignItems:"center", width:"480px", pointerEvents:"none",
      }}>
        {notifs.map(n=>(
          <div key={n.id} style={{
            background:"#fff", borderLeft:"5px solid "+ntC[n.type],
            border:"1.5px solid "+ntC[n.type]+"66", borderRadius:9,
            padding:"7px 14px", display:"flex", alignItems:"center", gap:10,
            boxShadow:"0 4px 16px "+ntC[n.type]+"22", width:"100%",
            animation:"notif-in .35s cubic-bezier(.22,1,.36,1)", pointerEvents:"auto",
          }}>
            <div style={{
              width:20, height:20, borderRadius:"50%", background:ntC[n.type],
              color:"#fff", display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:11, fontWeight:800, flexShrink:0,
            }}>{n.type==="green"?"✓":n.type==="orange"?"⏱":n.type==="red"?"●":"i"}</div>
            <div style={{flex:1, fontSize:12, fontWeight:700, color:"#333"}}>{n.text}</div>
            <div style={{fontSize:10, color:GRAY_L, whiteSpace:"nowrap"}}>{n.time}</div>
          </div>
        ))}
      </div>

      {/* TITLE */}
      <div style={{textAlign:"center", padding:"14px 0 8px"}}>
        <div style={{fontSize:15, fontWeight:800, color:TEAL, letterSpacing:3}}>SYSTÈME DE BARRIÈRE AUTOMATIQUE</div>
        <div style={{fontSize:10, color:GRAY_L, letterSpacing:1, marginTop:2}}>Supervision temps réel — {clock}</div>
      </div>

      {(!connected || stale || error) && (
        <div role="alert" style={{
          maxWidth:1068, margin:"0 auto 10px", padding:"8px 12px", borderRadius:9,
          background:"#fff4e0", border:"1px solid #ffd9a0", color:"#8a5200",
          fontSize:11, fontWeight:800,
        }}>
          {!connected ? "BACKEND DÉCONNECTÉ — dernière valeur connue conservée"
            : stale ? "DONNÉES OBSOLÈTES — commandes désactivées"
            : "ERREUR DE DONNÉES"}
          {error ? ` — ${error}` : ""}
        </div>
      )}

      {/* MAIN 3-COLUMN GRID */}
      <div style={{
        display:"grid",
        gridTemplateColumns:"160px 1fr 160px",
        gap:12,
        padding:"0 16px 16px",
        maxWidth:1100,
        margin:"0 auto",
      }}>

        {/* ── COLONNE GAUCHE ── */}
        <div style={{display:"flex", flexDirection:"column", gap:10}}>

          <div className="glass-card">
            <div className="s-title">COMMANDES</div>
            <button onClick={detectEntry} disabled={controlsDisabled || !fcClose} style={{
              width:"100%", border:"none", borderRadius:9,
              cursor:controlsDisabled || !fcClose ? "not-allowed" : "pointer",
              marginBottom:8, overflow:"hidden",
              opacity:(!controlsDisabled && fcClose && !opening && !closing)?1:0.5, transition:".2s",
              boxShadow:capteurE?"0 0 12px #057c8255":"none",
            }}>
              <div style={{background:TEAL, padding:"11px 10px", color:"#fff",
                display:"flex", alignItems:"center", gap:8}}>
                <span style={{fontSize:20}}>🚗</span>
                <div style={{fontSize:11, fontWeight:800, letterSpacing:1, lineHeight:1.3, textAlign:"left"}}>
                  DÉTECTION<br/>ENTRÉE VÉHICULE
                </div>
              </div>
            </button>
            <button onClick={detectExit} disabled={controlsDisabled || !fcOpen} style={{
              width:"100%", border:"none", borderRadius:9,
              cursor:controlsDisabled || !fcOpen ? "not-allowed" : "pointer",
              overflow:"hidden",
              opacity:(!controlsDisabled && fcOpen && !opening && !closing)?1:0.5, transition:".2s",
              boxShadow:capteurS?"0 0 12px #e67e2255":"none",
            }}>
              <div style={{background:ORANGE, padding:"11px 10px", color:"#fff",
                display:"flex", alignItems:"center", gap:8}}>
                <span style={{fontSize:20}}>🚗</span>
                <div style={{fontSize:11, fontWeight:800, letterSpacing:1, lineHeight:1.3, textAlign:"left"}}>
                  DÉTECTION<br/>SORTIE VÉHICULE
                </div>
              </div>
            </button>
          </div>

          <div className="glass-card">
            <div className="s-title">INDICATEURS LED</div>
            <Indicator on={ledRouge} color="red"   title="LED rouge" sub="Barrière fermée"/>
            <Indicator on={ledVerte} color="green" title="LED verte"  sub="Passage autorisé"/>
          </div>

          <div className="glass-card">
            <div className="s-title">CAPTEURS / ENTRÉES</div>
            <Indicator on={capteurE} color="green" title="Capteur entrée"  sub="Détection d'entrée"/>
            <Indicator on={capteurS} color="green" title="Capteur sortie"  sub="Détection de sortie"/>
          </div>
        </div>

        {/* ── CENTRE : SVG taille naturelle ── */}
        <div className="glass-card" style={{padding:0, overflow:"hidden"}}>
          <BarrierScene
            angle={angle} carX={carX} carVisible={carVisible}
            opening={opening} closing={closing}
          />
        </div>

        {/* ── COLONNE DROITE ── */}
        <div style={{display:"flex", flexDirection:"column", gap:10}}>

          <div className="glass-card">
            <div className="s-title">FIN DE COURSE</div>
            <StateTile on={fcOpen}  color="green" title="Fin de course ouv."  sub="Barrière ouverte"/>
            <StateTile on={fcClose} color="red"   title="Fin de course ferm." sub="Barrière fermée"/>
          </div>

          <div className="glass-card">
            <div className="s-title">SORTIES / ACTIONS</div>
            <StateTile on={motorS1}    color="green"  title="Moteur sens 1" sub="Ouverture"/>
            <StateTile on={motorS2}    color="orange" title="Moteur sens 2" sub="Fermeture"/>
            <StateTile on={motorsIdle} color="teal"    title="Moteur en arrêt"  sub="Aucun mouvement"/>
          </div>

          <div className="glass-card">
            <div className="s-title">ÉTAT SYSTÈME</div>
            <StatusRow label="État"      value={sysLabel}     valColor={sysColor}/>
            <StatusRow label="Position"  value={positionLabel} valColor={TEAL}/>
            <StatusRow label="Moteur"    value={(opening||closing)?"MARCHE":"ARRÊT"} valColor={(opening||closing)?GREEN:GRAY_L}/>
            <StatusRow label="Sens"      value={opening?"SENS 1 ↑":closing?"SENS 2 ↓":"—"} valColor={opening?TEAL:closing?ORANGE:GRAY_L}/>
            <StatusRow label="Véhicule"  value={vehicleLabel} valColor={vehicleLabel==="AUCUN"?GRAY_L:GREEN}/>
          </div>

        </div>
      </div>

      {/* STATISTIQUES en bas — remplace l'ancien graphique historique */}
      <div style={{padding:"0 16px 20px", maxWidth:1100, margin:"0 auto"}}>
        <div className="glass-card">
          <div className="s-title">STATISTIQUES DE PASSAGE — TRAÇABILITÉ TEMPS RÉEL</div>
          <div style={{ display:"flex", gap:14, flexWrap:"wrap" }}>

            <div style={{
              flex:"1 1 200px", textAlign:"center", padding:"16px 12px",
              background:TEAL_BG, borderRadius:10, border:"1px solid "+BORDER2,
            }}>
              <div style={{fontSize:9, fontWeight:800, color:GRAY_L, letterSpacing:1.5, marginBottom:8}}>
                VÉHICULES ENTRÉS
              </div>
              <div style={{display:"flex", alignItems:"baseline", justifyContent:"center", gap:8}}>
                <span style={{fontSize:20}}>🚗</span>
                <span style={{fontSize:38, fontWeight:800, color:TEAL, lineHeight:1, fontFamily:"Rajdhani,monospace"}}>
                  {entryCount ?? "—"}
                </span>
              </div>
              <div style={{fontSize:9, color:GRAY_L, marginTop:6}}>
                {isSimulation ? "depuis le démarrage du simulateur" : "non fourni par le PLC"}
              </div>
            </div>

            <div style={{
              flex:"1 1 200px", textAlign:"center", padding:"16px 12px",
              background:TEAL_BG, borderRadius:10, border:"1px solid "+BORDER2,
            }}>
              <div style={{fontSize:9, fontWeight:800, color:GRAY_L, letterSpacing:1.5, marginBottom:8}}>
                SOURCE DES DONNÉES
              </div>
              <div style={{display:"flex", alignItems:"baseline", justifyContent:"center", gap:8}}>
                <span style={{fontSize:20}}>{isSimulation ? "🧪" : dataSource === "plc" ? "🏭" : "?"}</span>
                <span style={{fontSize:20, fontWeight:800, color:ORANGE, lineHeight:1, fontFamily:"Rajdhani,monospace"}}>
                  {dataSourceLabel}
                </span>
              </div>
            
            </div>

            <div style={{
              flex:"1 1 200px", textAlign:"center", padding:"16px 12px",
              background:TEAL_BG, borderRadius:10, border:"1px solid "+BORDER2,
            }}>
              <div style={{fontSize:9, fontWeight:800, color:GRAY_L, letterSpacing:1.5, marginBottom:8}}>
                TEMPS DE FONCTIONNEMENT
              </div>
              <div style={{display:"flex", alignItems:"baseline", justifyContent:"center", gap:8}}>
                <span style={{fontSize:20}}>⏱</span>
                <span style={{fontSize:32, fontWeight:800, color:TEAL, lineHeight:1, fontFamily:"Rajdhani,monospace"}}>
                  {elapsed == null ? "—" : formatDuration(elapsed)}
                </span>
              </div>
              <div style={{fontSize:9, color:GRAY_L, marginTop:6}}>hh : mm : ss</div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
